import { notifications } from '@mantine/notifications';
import { AdPlatform } from '@marketing-tool/shared';
import type { AdControlLogic, Cohort, EntrySegmentation, Statistics } from '@marketing-tool/shared';
import type dayjs from 'dayjs';
import React from 'react';

import { trpc } from '../lib/trpc';
import { useAllPages, useTrpcQuery } from '../lib/trpc-hooks';
import { useMediaWithStats } from '../platforms/line/hooks';

import type { Ad, AdGroup, Campaign } from './game-report/types';
import * as remote from './remote-data';

export const useCampaigns = (
    gameId: string,
    platform: AdPlatform,
): { data: remote.RemoteData<Campaign[], string>; load: () => Promise<void> } => {
    const query = platform === AdPlatform.Line ? trpc.line.campaigns.list : trpc.meta.campaigns.list;
    return useAllPages((cursor, signal) => query.query({ gameId, cursor }, { signal }), [gameId, platform]);
};

export const useAdGroups = (
    gameId: string,
    platform: AdPlatform,
    campaignId?: string | number,
): { data: remote.RemoteData<AdGroup[], string>; load: () => Promise<void> } => {
    const cid = campaignId?.toString();
    const query = platform === AdPlatform.Line ? trpc.line.adGroups.list : trpc.meta.adGroups.list;
    return useAllPages(
        (cursor, signal) => query.query({ gameId, campaignId: cid, cursor }, { signal }),
        [gameId, platform, campaignId],
    );
};

export const useAds = (
    gameId: string,
    platform: AdPlatform,
    campaignId?: string | number,
    adGroupId?: string | number,
): { data: remote.RemoteData<Ad[], string>; load: () => Promise<void> } => {
    const cid = campaignId?.toString();
    const agid = adGroupId?.toString();
    const query = platform === AdPlatform.Line ? trpc.line.ads.list : trpc.meta.ads.list;
    return useAllPages(
        (cursor, signal) => query.query({ gameId, campaignId: cid, adGroupId: agid, cursor }, { signal }),
        [gameId, platform, campaignId, adGroupId],
    );
};

export const useCohorts = (gameId: string): { data: remote.RemoteData<Cohort[], string>; load: () => Promise<void> } =>
    useTrpcQuery((signal) => trpc.games.amplitude.cohorts.query({ gameId }, { signal }), [gameId]);

export const useMedia = (
    gameId: string,
): {
    data: remote.RemoteData<
        {
            id: number;
            type: 'IMAGE' | 'VIDEO' | 'APP' | 'URL' | 'ANIMATION';
            name: string;
            url?: string;
            createdDate?: string;
            ads: { id: string; name: string; cost: number; currency: string }[];
        }[],
        string
    >;
    load: () => Promise<void>;
} => useMediaWithStats(gameId);

export const useStatistics = (
    gameId: string,
    platform: AdPlatform,
    adGroupId: string | number,
    from: dayjs.Dayjs,
    to: dayjs.Dayjs,
): { data: remote.RemoteData<Statistics[], string>; load: () => Promise<void> } => {
    const agid = adGroupId.toString();
    const fromStr = from.format('YYYY-MM-DD');
    const toStr = to.format('YYYY-MM-DD');
    const query = platform === AdPlatform.Line ? trpc.line.statistics.list : trpc.meta.statistics.list;
    return useAllPages(
        (cursor, signal) => query.query({ gameId, adGroupId: agid, from: fromStr, to: toStr, cursor }, { signal }),
        [gameId, platform, adGroupId, from.valueOf(), to.valueOf()],
    );
};

export const useEntrySegmentation = (
    gameId: string,
    from: dayjs.Dayjs,
    to: dayjs.Dayjs,
    day: 0 | 1,
    cohortId?: string,
): {
    data: remote.RemoteData<EntrySegmentation[], string>;
    load: () => Promise<void>;
} => {
    const adjustedFrom = from.add(day, 'day');
    const adjustedTo = to.add(day, 'day');
    return useTrpcQuery(
        (signal) =>
            trpc.games.amplitude.entries.query(
                {
                    gameId,
                    from: adjustedFrom.format('YYYY-MM-DD'),
                    to: adjustedTo.format('YYYY-MM-DD'),
                    day: day.toString() as '0' | '1',
                    cohortId,
                },
                { signal },
            ),
        [gameId, adjustedFrom.valueOf(), adjustedTo.valueOf(), cohortId],
    );
};

export const useAdControlLogic = (gameId: string, platform: AdPlatform | undefined) => {
    const [adControlLogic, setAdControlLogic] = React.useState<remote.RemoteData<AdControlLogic, string>>(
        remote.NotAsked,
    );
    const ref = React.useRef(0);

    const api = React.useMemo(
        () => ({
            async load() {
                if (!platform) return;

                const id = ++ref.current;

                const ifCurrent = (cb: () => void) => {
                    if (id === ref.current) {
                        cb();
                    }
                };

                ifCurrent(() => {
                    setAdControlLogic(remote.Loading);
                });

                try {
                    const result = await trpc.games.adControl.get.query({ gameId, platform });
                    ifCurrent(() => setAdControlLogic(remote.Success(result)));
                } catch (error) {
                    ifCurrent(() =>
                        setAdControlLogic(remote.Failure(error instanceof Error ? error.message : String(error))),
                    );
                }
            },

            async update(
                type: 'campaigns' | 'adGroups',
                id: string,
                days: number,
                logic: string,
                allowAutoStart: boolean,
                minActiveAdCount: number,
            ) {
                if (!platform) return;

                try {
                    await trpc.games.adControl.update.mutate({
                        gameId,
                        platform,
                        type,
                        id,
                        days,
                        logic,
                        allowAutoStart,
                        minActiveAdCount,
                    });

                    notifications.show({ title: 'Success', message: 'Ad control logic updated', color: 'green' });
                    setAdControlLogic((adControlLogic) =>
                        remote.map(adControlLogic, (adControlLogic) => ({
                            ...adControlLogic,
                            [type]: {
                                ...adControlLogic[type],
                                [id]: { days, logic, allowAutoStart, minActiveAdCount },
                            },
                        })),
                    );
                } catch (error) {
                    notifications.show({
                        title: 'Error',
                        message: error instanceof Error ? error.message : 'Failed to update ad control logic',
                        color: 'red',
                    });
                }
            },

            async delete(type: 'campaigns' | 'adGroups', id: string) {
                if (!platform) return;

                try {
                    await trpc.games.adControl.delete.mutate({
                        gameId,
                        platform,
                        type,
                        id,
                    });

                    notifications.show({ title: 'Success', message: 'Ad control logic deleted', color: 'green' });
                    setAdControlLogic((adControlLogic) =>
                        remote.map(adControlLogic, (adControlLogic) => {
                            const { [id]: _, ...rest } = adControlLogic[type];
                            return {
                                ...adControlLogic,
                                [type]: rest,
                            };
                        }),
                    );
                } catch (error) {
                    notifications.show({
                        title: 'Error',
                        message: error instanceof Error ? error.message : 'Failed to delete ad control logic',
                        color: 'red',
                    });
                }
            },
        }),
        [gameId, platform, ref, setAdControlLogic],
    );

    React.useEffect(() => {
        void api.load();
    }, [api]);

    return React.useMemo(() => ({ data: adControlLogic, ...api }), [adControlLogic, api]);
};
